import apiClient from './config';
import { ValidateTokenResponse, SubmitSignatureResponse } from '../../types/signatureToken';
import { dataURLtoBlob } from '../../utils/signatureUtils';

// ========================================
// SERVICE PARA SIGNATURE TOKENS
// ========================================

/**
 * Validar token de firma y obtener datos del contrato
 * GET /api/firmar/:token
 * 
 * IMPORTANTE: Este endpoint es PÚBLICO (no requiere JWT)
 * 
 * Valida:
 * - Si el token existe
 * - Si no ha expirado
 * - Si no ha sido usado
 * 
 * @param token - UUID del token de firma
 * @returns Información del contrato y cliente si es válido, o mensaje de error
 */
export const validateSignatureToken = async (
    token: string
): Promise<ValidateTokenResponse> => {
    console.log('🔐 Validating signature token:', token);
    
    try {
        const response = await apiClient.get(`/firmar/${token}`);
        console.log('✅ Token validation response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('❌ Token validation error:', error);
        
        // Si el backend retorna 400/404 con estructura de error válida
        if (error.response?.data) {
        return error.response.data;
        }
        
        // Error de red u otro tipo de error
        throw error;
    }
};

/**
 * Enviar firma de contrato
 * POST /api/firmar/:token
 * 
 * IMPORTANTE: Este endpoint es PÚBLICO (no requiere JWT)
 * 
 * @param token - UUID del token de firma
 * @param clientDni - DNI del cliente (debe coincidir con el del token)
 * @param signatureDataURL - DataURL de la firma (base64)
 * @returns Confirmación de firma exitosa o mensaje de error
 */
export const submitSignature = async (
    token: string,
    clientDni: string,
    signatureDataURL: string
): Promise<SubmitSignatureResponse> => {
    console.log('📝 Submitting signature for token:', token);
    
    try {
        // Convertir dataURL a Blob
        const signatureBlob = dataURLtoBlob(signatureDataURL);
        
        // Crear FormData
        const formData = new FormData();
        formData.append('clientDni', clientDni);
        formData.append('signature', signatureBlob, 'firma.png');
        
        console.log('📤 Sending FormData with DNI:', clientDni);
        console.log('📤 Signature blob size:', signatureBlob.size, 'bytes');
        console.log('📤 Signature blob type:', signatureBlob.type);
        console.log('📤 FormData entries:', Array.from(formData.entries()).map(([key, value]) => ({
            key,
            value: value instanceof Blob ? `Blob(${value.size} bytes, ${value.type})` : value
        })));
        
        // Enviar al backend (eliminar Content-Type para que el navegador lo configure automáticamente)
        const response = await apiClient.post(`/firmar/${token}`, formData, {
            headers: {
                'Content-Type': undefined, // Importante: eliminar el Content-Type default de apiClient
            },
            // Log para debugging
            onUploadProgress: (progressEvent) => {
                console.log('📤 Upload progress:', progressEvent.loaded, '/', progressEvent.total);
            }
        });
        
        console.log('✅ Signature submitted successfully:', response.data);
        return response.data;
        
    } catch (error: any) {
        console.error('❌ Signature submission error:', error);
        console.error('❌ Backend response data:', error.response?.data);
        console.error('❌ Backend response status:', error.response?.status);
        
        // Si el backend retorna error con estructura válida
        if (error.response?.data) {
            return error.response.data;
        }
        
        // Error de red u otro tipo de error
        throw error;
    }
};
